import { useEffect, useRef } from 'react';

/**
 * Honeypot bot protection — S-03 remediation (replaces Cloudflare Turnstile).
 *
 * Renders two invisible fields inside a form:
 *   1. `website` — a text input that is off-screen (not display:none — bots
 *      skip that), aria-hidden, and marked autocomplete="off". Real users
 *      never see or fill it; simple form-spam bots always do.
 *   2. `form_started_at` — an ISO timestamp stamped on mount. The backend
 *      enforces a minimum dwell time (~1.5s) between mount and submit.
 *
 * On a triggered honeypot the server returns a fake 200 so bots cannot
 * learn the detection method. See backend/rate_limit.py::check_honeypot.
 *
 * Usage:
 *   const hp = useHoneypot();
 *   ...
 *   await api.post('/auth/register', { ...payload, ...hp.getValues() });
 *   <input ref={hp.ref} ... />   (rendered via <HoneypotFields /> below)
 */

export const useHoneypot = () => {
  const websiteRef = useRef(null);
  const startedAt = useRef(new Date().toISOString());

  useEffect(() => {
    // Reset the timestamp every time the component (form) is mounted so a
    // legitimate user always gets a fresh dwell-time baseline.
    startedAt.current = new Date().toISOString();
  }, []);

  const getValues = () => ({
    website: websiteRef.current?.value || '',
    form_started_at: startedAt.current,
  });

  return { websiteRef, getValues };
};

export const HoneypotFields = ({ inputRef }) => (
  // Off-screen but still in the DOM and focusable by bots that iterate inputs.
  // NOT display:none — most bots skip display:none inputs. Position:absolute
  // + off-screen coordinates is the standard accessible-honeypot pattern.
  <div
    aria-hidden="true"
    style={{
      position: 'absolute',
      left: '-10000px',
      top: 'auto',
      width: '1px',
      height: '1px',
      overflow: 'hidden',
    }}
  >
    <label htmlFor="hp-website" aria-hidden="true">
      Website (leave blank)
      <input
        id="hp-website"
        ref={inputRef}
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        defaultValue=""
      />
    </label>
  </div>
);

export default HoneypotFields;
